/**
 * @description Used to detect a key for a duplicated item that has not been not saved (e.g. "paid_users_copy_")
 */
export const DUPLICATED_KEY_REGEX = /_copy_?$/;

/**
 * @description Character length enforced by the backend for all names
 */
export const MAX_NAME_CHARACTER_LENGTH = 255;

/**
 * @description Character length enforced by the backend for all keys
 */
export const MAX_KEY_CHARACTER_LENGTH = 64;

/**
 * @description Used to detect a key for a new item that has not been not saved (e.g. "new!1601910762830")
 */
export const NEW_KEY_REGEX = /new!\d+$/;

/**
 * @description Used to detect a key for an item that has been deleted but not saved (e.g. "my_rule_key!deleted_1601910762830")
 */
export const DELETED_KEY_REGEX = /!deleted_\d+$/;

/**
 * @description Specifies which characters are not allowed in a key. Used to replace disallowed characters with allowed characters
 * @see https://github.com/optimizely/optimizely/blob/8fbea63976324566eef525fce4c2e997d3a24e3d/src/www/constants/experimentation.py#L12
 */
export const KEY_DISALLOWED_CHARACTERS = /[^a-z0-9_-]|\s+/gi;

/**
 * @description Find the rule key (e.g. "my_targeted_delivery") from a path like '/rules/my_targeted_delivery'
 *  but ignore paths like '/rules/my_targeted_delivery/variations/on'
 */
export const RULE_PATCH_PATH_KEY_MATCH = /^\/rules\/(\w+)$/;

/**
 * @description The default columns to show on the Flags List
 */
export const FLAGS_LIST_DEFAULT_CONFIGURABLE_COLUMNS_KEYS = [
  'name',
  'key',
  'rules_in_environment',
  'enabled_in_environment',
  'created_time',
  'updated_time',
];

/**
 * @description  All columns keys for Flags List which can have visibility toggled on/off
 * (this is currently the same as FLAGS_LIST_DEFAULT_CONFIGURABLE_COLUMNS_KEYS, but that should
 * change as we add more supported columns)
 */
export const FLAGS_LIST_ALL_CONFIGURABLE_COLUMNS_KEYS = [
  'name',
  'key',
  'rules_in_environment',
  'enabled_in_environment',
  'created_time',
  'updated_time',
  'created_by_user_email',
];

export const EXPANDABLE_FLAGS_LIST_DEFAULT_CONFIGURABLE_COLUMNS_KEYS = [
  'audience',
  'created_time',
  'days_running',
  'key',
  'name',
  'primary_metric',
  'status',
  'traffic_allocation',
  'type',
  'updated_time',
  'variations',
  'results',
];

export const EXPANDABLE_FLAGS_LIST_ALL_CONFIGURABLE_COLUMNS_KEYS = [
  'audience',
  'created_by_user_email',
  'created_time',
  'days_running',
  'key',
  'name',
  'primary_metric',
  'status',
  'traffic_allocation',
  'type',
  'updated_time',
  'variations',
  'results',
];

export const FLAGS_LIST_UNCONFIGURABLE_COLUMNS_KEYS = ['ellipsis'];

/**
 * @description  All the filter types that can be configured on the 'Flags List' via
 * the 'Table Options' panel's 'Filters' tab
 * (Requires 'fx_flags_dashboard_flexible_columns' and 'flags_flex_dashboard' feature flags to be enabled)
 */
export const FLAGS_LIST_FILTER_TYPES = {
  Flag: {
    category: 'Flag',
    values: [
      {
        key: 'archived',
        menuLabel: 'Archive status',
        selectedLabel: 'Flag archive status',
        value: 'archived',
      },
    ],
  },
  // Order of keys in this object can be non-alphabetical
  // because it is used to render dropdown menu
  // eslint-disable-next-line sort-keys-fix/sort-keys-fix
  Environment: {
    category: 'Environment',
    values: [
      {
        key: 'environment_type',
        menuLabel: 'Type',
        selectedLabel: 'Environment type',
        value: 'environment_type',
      },
      {
        key: 'environment_status',
        menuLabel: 'Status',
        selectedLabel: 'Environment status',
        value: 'environment_status',
      },
    ],
  },
  Rule: {
    category: 'Rule',
    values: [
      {
        key: 'rule_type',
        menuLabel: 'Type',
        selectedLabel: 'Rule type',
        value: 'rule_type',
      },
      {
        key: 'rule_status',
        menuLabel: 'Status',
        selectedLabel: 'Rule status',
        value: 'rule_status',
      },
    ],
  },
};

export const FLAGS_LIST_FILTER_TYPE_KEYS = Object.values(
  FLAGS_LIST_FILTER_TYPES
).reduce((acc: String[], item) => {
  const { values } = item;
  for (let el of values) {
    const { key } = el;
    acc.push(key);
  }
  return acc;
}, []);

export const FLAGS_LIST_FILTER_TYPE_LABELS = Object.values(
  FLAGS_LIST_FILTER_TYPES
).reduce((acc: { [key: string]: string }, item) => {
  const { values } = item;
  for (let el of values) {
    const { selectedLabel, value } = el;
    acc[value] = selectedLabel;
  }
  return acc;
}, {});

export const FLAGS_LIST_FILTER_TYPE_VALUE_TO_CATEGORY_MAP = Object.values(
  FLAGS_LIST_FILTER_TYPES
).reduce((acc: { [key: string]: string }, item) => {
  const { values, category } = item;
  for (let el of values) {
    const { value } = el;
    acc[value] = category;
  }
  return acc;
}, {});

export const FLAGS_LIST_IS_FILTER_OPERATOR = { label: 'is', value: 'is' };
export const FLAGS_LIST_IS_ANY_OF_FILTER_OPERATOR = {
  label: 'is any of',
  value: 'any',
};
export const FLAGS_LIST_IS_NOT_OPERATOR = { label: 'is not', value: 'not' };

export const FLAGS_LIST_ALL_FILTER_OPERATORS = [
  FLAGS_LIST_IS_FILTER_OPERATOR,
  FLAGS_LIST_IS_ANY_OF_FILTER_OPERATOR,
  FLAGS_LIST_IS_NOT_OPERATOR,
];

export const FLAGS_LIST_ALL_FILTER_OPERATOR_LABELS = FLAGS_LIST_ALL_FILTER_OPERATORS.reduce(
  (acc: { [key: string]: string }, item) => {
    const { label, value } = item;
    acc[value] = label;
    return acc;
  },
  {}
);

export const FLAG_ARCHIVED_FILTER_VALUES: { [key: string]: any } = {
  false: {
    key: 'false',
    label: 'No',
  },
  true: {
    key: 'true',
    label: 'Yes',
  },
};

export const ENVIRONMENT_STATUS_FILTER_VALUES: { [key: string]: any } = {
  draft: {
    key: 'draft',
    label: 'Draft',
  },
  paused: {
    key: 'paused',
    label: 'Paused',
  },
  running: {
    key: 'running',
    label: 'Running',
  },
};

export const RULE_TYPE_FILTER_VALUES: { [key: string]: any } = {
  'a/b': {
    key: 'a/b',
    label: 'A/B Test',
  },
  multi_armed_bandit: {
    key: 'multi_armed_bandit',
    label: 'Multi-Armed Bandit',
  },
  targeted_delivery: {
    key: 'targeted_delivery',
    label: 'Targeted Delivery',
  },
};

export const RULE_STATUS_FILTER_VALUES: { [key: string]: any } = {
  draft: {
    key: 'draft',
    label: 'Draft',
  },
  paused: {
    key: 'paused',
    label: 'Paused',
  },
  ready_to_run: {
    key: 'ready_to_run',
    label: 'Ready to Run',
  },
  running: {
    key: 'running',
    label: 'Running',
  },
  // eslint-disable-next-line sort-keys-fix/sort-keys-fix
  concluded: {
    key: 'concluded',
    label: 'Concluded',
  },
};

export const TABLE_FILTER_DEFAULT_VALUE = {
  operator: null,
  type: null,
  value: [],
};
